<script setup lang="ts">
import type { Ref } from 'vue'

import ScenarioInformation from '@/views/scenario/ScenarioInformation.vue'
import ScenarioSettings from '@/views/scenario/ScenarioSettings.vue'
import ScenarioParameters from '@/views/scenario/ScenarioParameters.vue'
import ScenarioQuestions from '@/views/scenario/ScenarioQuestions.vue'
import { Modules } from '@/types/generated'
import ScenarioInformationData = Modules.Scenario.ScenarioInformationData
import ScenarioSettingsData = Modules.Scenario.ScenarioSettingsData
import ScenarioParameterData = Modules.Scenario.ScenarioParameterData
import ScenarioQuestionsData = Modules.Scenario.ScenarioQuestionsData
import ScenarioQuestionData = Modules.Scenario.ScenarioQuestionData
import ScenarioAnswerCombinationData = Modules.Scenario.ScenarioAnswerCombinationData
import ScenarioQuestionAnswerData = Modules.Scenario.ScenarioQuestionAnswerData
import ScenarioData = Modules.Scenario.ScenarioData
import { useScenarioStore } from '@core/stores/scenario'

definePage({
  meta: {
    action: 'manage',
    subject: 'scenarios',
  },
})

const createScenarioSteps = [
  {
    title: 'scenario.steps.information.title',
    subtitle: 'scenario.steps.information.subtitle',
    icon: 'tabler-file',
  },
  {
    title: 'scenario.steps.settings.title',
    subtitle: 'scenario.steps.settings.subtitle',
    icon: 'tabler-adjustments-horizontal',
  },
  {
    title: 'scenario.steps.parameters.title',
    subtitle: 'scenario.steps.parameters.subtitle',
    icon: 'tabler-briefcase',
  },
  {
    title: 'scenario.steps.questions.title',
    subtitle: 'scenario.steps.questions.subtitle',
    icon: 'tabler-help',
  },
]

const currentStep = ref(0)
const isCurrentStepValid = ref(true)
const route = useRoute('games-scenarios-id')
const router = useRouter()
const { t } = useI18n()
const saved = ref<boolean>(false)

const informationForm = ref<ScenarioInformationData>({
  title: '',
  has_img: false,
  competence_id: '',
  description: '',
  next_scenario_id: null,
  memory_pass_to_next: false,
  memory_pass_evaluation_to_next: false,
  memory_evaluate_next_with_agreements: false,
  description_for_team_a: '',
  description_for_team_b: '',
  ai_profile_id: null,
  ai_description: null,
  ai_description_card: null,
  knowledge_documents: [],
  access_level: Modules.Scenario.Enums.ScenarioAccessLevelEnum.MANAGER,
})

const settingsForm = ref<ScenarioSettingsData>({
  min_players: 2,
  max_players: 2,
  preparation_time: 0,
  protocol_time: 60,
  game_duration: 20,
  auto_show_results: true,
  is_team: false,
  is_camera_required: false,
  is_self_rated: true,
  is_each_other_rated: true,
  is_expert_rated: true,
  is_ai_rated: true,
})

const parametersForm = ref<ScenarioParameterData[]>([
  {
    id: '',
    title: '',
    values: [
      {
        id: '',
        team: 'A',
        points: 1,
        ratio: 1,
      },
      {
        id: '',
        team: 'B',
        points: 1,
        ratio: 1,
      },
    ],
  },
])

const questionsForm = ref<ScenarioQuestionsData>({
  questions: [],
  answerCombinations: [],
})

const scenarioData = ref<ScenarioData>({
  id: '',
  settings: settingsForm.value,
  information: informationForm.value,
  parameters: parametersForm.value,
  questions: questionsForm.value,
})

const getScenarioData = async () => {
  const { data: apiData } = await useApi<ScenarioData>(`/scenarios/${route.params.id}`)

  if (apiData.value && apiData?.value.information) {
    scenarioData.value = apiData.value

    informationForm.value = scenarioData.value.information
    informationForm.value.knowledge_documents = scenarioData.value.knowledgeDocuments ?? []
    settingsForm.value = scenarioData.value.settings
    parametersForm.value = scenarioData.value.parameters
    questionsForm.value = scenarioData.value.questions
  }
}

const scenarioStore = useScenarioStore()

// eslint-disable-next-line max-statements-per-line
if (route.params.id !== 'add') { await getScenarioData() }
else {
  if (JSON.stringify(scenarioStore.scenarioInformation) !== JSON.stringify({}))
    informationForm.value = scenarioStore.scenarioInformation
  if (JSON.stringify(scenarioStore.scenarioParameters) !== JSON.stringify({}))
    parametersForm.value = scenarioStore.scenarioParameters
  if (JSON.stringify(scenarioStore.scenarioQuestions) !== JSON.stringify({}))
    questionsForm.value = scenarioStore.scenarioQuestions
  if (JSON.stringify(scenarioStore.scenarioInformation) !== JSON.stringify({}))
    settingsForm.value = scenarioStore.scenarioSettings
}

const scrollToFirstError = () => {
  const errorField = document.querySelector('.v-field--error')
  if (!errorField)
    return
  errorField.scrollIntoView({ block: 'center', behavior: 'smooth' })

  const scrolledY = window.scrollY

  if (scrolledY)
    window.scroll(0, scrolledY - 140)
}

const descriptionIsValid = () => {
  if (
    informationForm.value?.description !== undefined
    && informationForm.value?.description !== ''
    && informationForm.value?.description_for_team_a !== undefined
    && informationForm.value?.description_for_team_a !== ''
    && informationForm.value?.description_for_team_b !== undefined
    && informationForm.value?.description_for_team_b !== ''
  )
    return true

  let hasOne = false
  if (informationForm.value?.description === undefined || informationForm.value?.description === '') {
    document.querySelector('.info-description')?.parentElement?.parentElement?.classList.add('v-field--error')
    hasOne = true
  }

  if (informationForm.value?.description_for_team_a === undefined || informationForm.value?.description_for_team_a === '') {
    document.querySelector('.info-description-a')?.parentElement?.parentElement?.classList.add('v-field--error')
    hasOne = true
  }

  if ((informationForm.value?.description_for_team_b === undefined || informationForm.value?.description_for_team_b === '')) {
    if (informationForm.value.ai_profile_id === null)
      document.querySelector('.info-description-b')?.parentElement?.parentElement?.classList.add('v-field--error')
    else
      return !hasOne
  }

  return false
}

const validateForm = (form: Ref) => {
  form.value?.validate().then((valid: any) => {
    if (valid.valid && descriptionIsValid()) {
      currentStep.value++
      isCurrentStepValid.value = true
    }
    else {
      isCurrentStepValid.value = false
      scrollToFirstError()
    }
  })
}

const addParameter = (value: ScenarioParameterData) => {
  parametersForm.value.push(value)
}

const removeParameter = (id: number) => {
  parametersForm.value.splice(id, 1)
}

const addQuestion = (value: ScenarioQuestionData) => {
  questionsForm.value.questions.push(value)
}

const addCombination = (value: ScenarioAnswerCombinationData) => {
  questionsForm.value.answerCombinations.push(value)
}

const addAnswer = (value: ScenarioQuestionAnswerData, index: number) => {
  questionsForm.value.questions[index].answers.push(value)
}

const removeQuestion = (id: number) => {
  questionsForm.value.questions.splice(id, 1)
  if (questionsForm.value.questions.length === 0)
    questionsForm.value.answerCombinations = []
}

const removeCombination = (id: number) => {
  questionsForm.value.answerCombinations.splice(id, 1)
}

const copyQuestion = (id: number) => {
  questionsForm.value.questions.push(structuredClone(toRaw(questionsForm.value.questions[id])))
}

const removeAnswer = (answerIndex: number, questionIndex: number) => {
  questionsForm.value.questions[questionIndex].answers.splice(answerIndex, 1)
}

const formInProcess = ref<boolean>(false)

const submitForm = async (form: Ref) => {
  form.value?.validate().then(async (valid: any) => {
    if (valid.valid && descriptionIsValid()) {
      currentStep.value++
      isCurrentStepValid.value = true

      let res: boolean = true
      try {
        scenarioData.value = {
          id: route.params.id,
          information: informationForm.value,
          settings: settingsForm.value,
          parameters: parametersForm.value,
          questions: questionsForm.value,
          knowledgeDocuments: informationForm.value.knowledge_documents ?? null,
        }

        const resp = await $api('/scenarios', {
          method: route.params.id === 'add' ? 'POST' : 'PUT',
          body: {
            ...scenarioData.value,
          },
          onResponseError({ response }) {
            console.log(response)
            res = false
          },
        })

        if (res) {
          saved.value = true

          scenarioStore.scenarioParameters = {}
          scenarioStore.scenarioInformation = {}
          scenarioStore.scenarioQuestions = {}
          scenarioStore.scenarioSettings = {}

          localStorage.removeItem('scenarioParameters')
          localStorage.removeItem('scenarioInformation')
          localStorage.removeItem('scenarioQuestions')
          localStorage.removeItem('scenarioSettings')
          if (route.params.id === 'add') {
            await router.push(`/games/scenarios/${resp.id}`)
            currentStep.value = 0
          }
        }
        formInProcess.value = false
      }
      catch (err) {
        formInProcess.value = false
      }
    }
    else {
      isCurrentStepValid.value = false
      scrollToFirstError()
    }
  })
}

type ScenarioStep = 'scenarioInformation' | 'scenarioParameters' | 'scenarioQuestions' | 'scenarioSettings'

const saveDraft = (data: { step: ScenarioStep; data: any }) => {
  if (scenarioData.value.id !== '')
    return

  localStorage.setItem(data.step, JSON.stringify(data.data))
  scenarioStore[data.step] = data.data
}
</script>

<template>
  <VSnackbar
    v-model="saved"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ t('scenario.saved') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="saved = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <div class="d-flex justify-space-between align-center flex-wrap gap-y-4 mb-6">
    <div>
      <div class="d-flex gap-2 align-center flex-wrap">
        <VIcon
          class="ma-1"
          size="22"
          icon="tabler-chevron-left"
          @click="$router.push('/games/scenarios/list')"
        />
        <h5
          v-if="scenarioData.id"
          class="text-h5"
        >
          {{ t('scenario.edit_detail') }}
        </h5>
        <h5
          v-else
          class="text-h5"
        >
          {{ t('scenario.add_detail') }}
        </h5>
      </div>
    </div>
  </div>
  <VCard>
    <VRow no-gutters>
      <VCol
        cols="12"
        md="4"
        lg="3"
        :class="$vuetify.display.mdAndUp ? 'border-e' : 'border-b'"
      >
        <VCardText>
          <AppStepper
            v-if="route.params.id === 'add'"
            v-model:current-step="currentStep"
            direction="vertical"
            :items="createScenarioSteps"
            :is-active-step-valid="isCurrentStepValid"
            icon-size="22"
            class="stepper-icon-step-bg"
          />
          <AppStepper
            v-else
            v-model:current-step="currentStep"
            direction="vertical"
            :items="createScenarioSteps"
            icon-size="22"
            class="stepper-icon-step-bg"
          />
        </VCardText>
      </VCol>

      <VCol
        cols="12"
        md="8"
        lg="9"
      >
        <VCardText>
          <VWindow
            v-model="currentStep"
            class="disable-tab-transition"
          >
            <VWindowItem>
              <ScenarioInformation
                v-model:form-data="informationForm"
                v-model:scenario-id="scenarioData.id"
                v-model:form-in-process="formInProcess"
                @validate="validateForm"
                @submit="submitForm"
                @save-draft="saveDraft"
              />
            </VWindowItem>

            <VWindowItem>
              <ScenarioSettings
                v-model:form-data="settingsForm"
                v-model:current-step="currentStep"
                :competence-id="informationForm.competence_id"
                @prev-step="currentStep--"
                @validate="validateForm"
                @save-draft="saveDraft"
              />
            </VWindowItem>

            <VWindowItem>
              <ScenarioParameters
                v-model:form-data="parametersForm"
                v-model:current-step="currentStep"
                @push="addParameter"
                @remove="removeParameter"
                @prev-step="currentStep--"
                @validate="validateForm"
                @save-draft="saveDraft"
              />
            </VWindowItem>

            <VWindowItem>
              <ScenarioQuestions
                v-model:form-data="questionsForm"
                v-model:current-step="currentStep"
                v-model:form-in-process="formInProcess"
                @prev-step="currentStep--"
                @submit="submitForm"
                @push="addQuestion"
                @push-answer="addAnswer"
                @remove="removeQuestion"
                @copy="copyQuestion"
                @remove-answer="removeAnswer"
                @push-combination="addCombination"
                @remove-combination="removeCombination"
                @save-draft="saveDraft"
              />
            </VWindowItem>
          </VWindow>
        </VCardText>
      </VCol>
    </VRow>
  </VCard>
</template>
